import { useQuery } from 'react-apollo';
import { useMemo } from 'react';

import { AlbumMembershipGroupForPurchasingOptionsCheck } from '../graphql/queries/album-membership-group';
import { ALBUM_SUBTYPE } from '../../../../../../constants/albums';
import { AlbumForPurchasingOptionsCheck } from '../graphql/queries/album';
import { createAlbumMembershipGroupEntity } from '../components/PhotoPurchasingOptions/helpers';

/**
 * A hook that checks if an AlbumMembershipGroup has purchasing options.
 * This is a simplified version of albumMembershipGroupHasPurchasingOptions that
 * only checks totals rather than delving deep into actual records.
 *
 * @param {Object} options The options.
 * @param {string} options.albumMembershipGroupId The album membership group id.
 * @param {string} options.albumId The album id.
 * @param {Object} options.identity The active Identity (optional).
 * @param {Boolean} options.sellPrintsEnabled If print sales are enabled.
 * @returns {Object} An object containing the result of the check.
 */
const useAlbumMembershipGroupPurchasingOptionsCheck = ({
  albumMembershipGroupId,
  albumId,
  identity,
  sellPrintsEnabled = true,
}) => {
  const {
    data: { albumMembershipGroup } = {},
    loading: loadingAlbumMembershipGroup,
  } = useQuery(AlbumMembershipGroupForPurchasingOptionsCheck, {
    variables: {
      id: albumMembershipGroupId,
    },
    skip: !albumMembershipGroupId,
    fetchPolicy: 'cache-first',
  });
  const { data: { album } = {}, loading: loadingAlbum } = useQuery(
    AlbumForPurchasingOptionsCheck,
    {
      variables: {
        id: albumId,
      },
      skip: !albumId,
      fetchPolicy: 'cache-first',
    },
  );

  const hasSubscriptionAvailable = useMemo(() => {
    const albumMembershipGroupEntity = createAlbumMembershipGroupEntity(
      albumMembershipGroup,
      album,
    );

    if (!albumMembershipGroupEntity) {
      return false;
    }

    const subscriptionAlbumFolderPrice =
      albumMembershipGroupEntity.album_folder?.price?.value || 0;
    const subscriptionAlbumPrice =
      albumMembershipGroupEntity.album?.price_whole_album?.value || 0;
    const rootAlbumFolderPrice =
      albumMembershipGroupEntity.album?.root_album_folder?.price?.value || 0;

    return (
      subscriptionAlbumFolderPrice > 0 ||
      subscriptionAlbumPrice > 0 ||
      rootAlbumFolderPrice > 0
    );
  }, [album, albumMembershipGroup]);

  /**
   * Checks if the AlbumMembershipGroup has purchasing options.
   * This is a simplified version that only checks totals rather than individual records.
   *
   * @returns {boolean} Whether the AlbumMembershipGroup has purchasing options.
   */
  const hasPurchasingOptions = useMemo(() => {
    const albumMembershipGroupEntity = createAlbumMembershipGroupEntity(
      albumMembershipGroup,
      album,
    );

    if (!albumMembershipGroupEntity) {
      return false;
    }

    const entity =
      albumMembershipGroupEntity.album ??
      albumMembershipGroupEntity.album_folder;

    // Find the identity in the albumMembershipGroup
    const albumMembershipGroupIdentity = identity
      ? albumMembershipGroupEntity.identities.find(
          amgIdentity => amgIdentity.identity.id === identity.id,
        )
      : null;

    const identityIsPurchased =
      albumMembershipGroupIdentity?.has_purchased_subscription ?? false;

    // Check if there's a subscription available
    const subscriptionAlbumFolderPrice =
      albumMembershipGroupEntity.album_folder?.price?.value || 0;
    const subscriptionAlbumPrice =
      albumMembershipGroupEntity.album?.price_whole_album?.value || 0;
    const rootAlbumFolderPrice =
      albumMembershipGroupEntity.album?.root_album_folder?.price?.value || 0;

    const hasSubscription =
      subscriptionAlbumFolderPrice > 0 ||
      subscriptionAlbumPrice > 0 ||
      rootAlbumFolderPrice > 0;

    // The Identity is optional, if it's available check if it hasn't purchased a sub already, otherwise
    // check if subs are generally available on the AlbumFolder/Album.
    const subscriptionAvailable = identity
      ? hasSubscription && !identityIsPurchased
      : hasSubscription;

    // Check if there are digital bundles
    const albumFolderDigitalBundles =
      albumMembershipGroupEntity.album_folder?.digital_bundles?.total || 0;
    const rootAlbumFolderDigitalBundles =
      albumMembershipGroupEntity.album?.root_album_folder?.digital_bundles
        ?.total || 0;
    const hasDigitalBundles =
      albumFolderDigitalBundles > 0 || rootAlbumFolderDigitalBundles > 0;

    // If PRO can't sell prints only check these 2
    if (!sellPrintsEnabled) {
      return subscriptionAvailable || hasDigitalBundles;
    }

    const rootAlbumFolderPrintPackages =
      albumMembershipGroupEntity.album?.root_album_folder
        ?.album_folder_print_packages?.total || 0;

    const hasAlbumPrintPackages =
      rootAlbumFolderPrintPackages ||
      (albumMembershipGroupEntity.print_packages?.length || 0) > 0;
    const hasPhotoPricing = !!albumMembershipGroupEntity.photo_pricing?.id;
    const hasAlbumCustomPrints =
      (albumMembershipGroupEntity.album_custom_prints?.total || 0) > 0;
    const hasAccountCardCustomPrints =
      (albumMembershipGroupEntity.account_card_album_custom_prints?.total ||
        0) > 0;
    const hasSpecialtyPrints =
      (albumMembershipGroupEntity.specialty_custom_prints?.total || 0) > 0;

    const isCampContent = entity?.album_subtype === ALBUM_SUBTYPE.CAMPS;

    const supportsLegacyPrints =
      entity?.search_subscription_credits_joins_enabled ||
      entity?.web_gallery_print_purchases_enabled ||
      entity?.pre_paid ||
      isCampContent;

    return (
      subscriptionAvailable ||
      hasAlbumPrintPackages ||
      hasPhotoPricing ||
      hasAlbumCustomPrints ||
      hasAccountCardCustomPrints ||
      hasSpecialtyPrints ||
      supportsLegacyPrints ||
      hasDigitalBundles
    );
  }, [sellPrintsEnabled, albumMembershipGroup, album]);

  return {
    loading: loadingAlbumMembershipGroup || loadingAlbum,
    hasPurchasingOptions,
    hasSubscriptionAvailable,
  };
};

export default useAlbumMembershipGroupPurchasingOptionsCheck;
